import { debounceTime } from "rxjs/operators";
import { ErrorHandler, Inject, Injectable } from "@angular/core";
import { Observable, Subject, fromEvent } from "rxjs";
import { GuidedTour, TourStep, Orientation, OrientationConfiguration } from "./guided-tour.constants";
import { cloneDeep } from "lodash";
import { DOCUMENT } from "@angular/common";
import { WindowRefService } from "./windowref.service";
import { Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { CloseSidebarAction, OpenSidebarAction, SelectOverlayAction } from "@edp-store/actions/layout.actions";
import { OnboardingService } from "@edp-services/onboarding.service";
import { OnboardingTypeEnum } from "@edp-enums/onboarding-type.enum";
import { sleep } from "@edp-utils/sleep.utils";

const indexZ = String(19999 + 2);

interface IDomElement {
    bottom: number;
    height: number;
    left: number;
    right: number;
    top: number;
    width: number;
    x: number;
    y: number;
}

@Injectable({
    providedIn: "root",
})
export class GuidedTourService {
    public guidedTourCurrentStepStream: Observable<TourStep>;
    public guidedTourOrbShowingStream: Observable<boolean>;

    private _guidedTourCurrentStepSubject = new Subject<TourStep>();
    private _guidedTourOrbShowingSubject = new Subject<boolean>();
    private _currentTourStepIndex = 0;
    private _currentTour: GuidedTour = null;
    private _onFirstStep = true;
    private _onLastStep = true;
    private _onResizeMessage = false;

    constructor(
        public errorHandler: ErrorHandler,
        private windowRef: WindowRefService,
        @Inject(DOCUMENT) private dom,
        private store: Store<IAppState>,
        private onboardingService: OnboardingService
    ) {
        this.guidedTourCurrentStepStream = this._guidedTourCurrentStepSubject.asObservable();
        this.guidedTourOrbShowingStream = this._guidedTourOrbShowingSubject.asObservable();

        fromEvent(this.windowRef.nativeWindow, "resize")
            .pipe(debounceTime(200))
            .subscribe(async () => {
                if (this._currentTour && this._currentTourStepIndex > -1) {
                    if (
                        this._currentTour.minimumScreenSize &&
                        this._currentTour.minimumScreenSize >= this.windowRef.nativeWindow.innerWidth
                    ) {
                        this._onResizeMessage = true;
                        const dialog = this._currentTour.resizeDialog || {
                            title: "Please resize",
                            content:
                                "You have resized the tour to a size that is too small to continue. Please resize the browser to a larger size to continue the tour or close the tour.",
                        };

                        this._guidedTourCurrentStepSubject.next(dialog);
                    } else {
                        this._onResizeMessage = false;
                        this._guidedTourCurrentStepSubject.next(await this.getPreparedTourStep(this._currentTourStepIndex));
                    }
                }
            });
    }

    public getOnboardingType() {
        return !!this._currentTour?.type ? this._currentTour.type.toString().toLowerCase() : null;
    }

    public nextStep(): void {
        const step = this._currentTour.steps[this._currentTourStepIndex];
        if (step.closeAction) {
            step.closeAction();
        }
        if (this._currentTour.steps[this._currentTourStepIndex + 1]) {
            this._currentTourStepIndex++;
            this._setFirstAndLast();
            if (step.action) {
                step.action();
            }
            this.onboardingService.setoperationHistoryService(this._currentTour.tourId, this._currentTour.type, "NEXT");
            this.goToStep("next");
        } else {
            this.onboardingService.setoperationHistoryService(this._currentTour.tourId, this._currentTour.type, "FINISH");
            if (this._currentTour.completeCallback) {
                this._currentTour.completeCallback();
            }
            this.resetTour();
        }
    }

    public backStep(): void {
        if (!!this.onFirstStep) {
            return;
        }
        if (this._currentTour.steps[this._currentTourStepIndex].closeAction) {
            this._currentTour.steps[this._currentTourStepIndex].closeAction();
        }
        if (this._currentTour.steps[this._currentTourStepIndex - 1]) {
            this._currentTourStepIndex--;
            this._setFirstAndLast();
            if (this._currentTour.steps[this._currentTourStepIndex].action) {
                this._currentTour.steps[this._currentTourStepIndex].action();
            }
            this.goToStep("back");
        } else {
            this.resetTour();
        }
    }

    public skipTour(sendOperationHistory: null | "CLOSE" | "OUTSIDE" = null): void {
        if (sendOperationHistory) {
            this.onboardingService.setoperationHistoryService(this._currentTour.tourId, this._currentTour.type, sendOperationHistory);
        }
        if (this._currentTour.skipCallback) {
            this._currentTour.skipCallback(this._currentTourStepIndex);
        }
        this._currentTour.completeCallback();
        this.resetTour();
    }

    private checkTemporaryElementToScroll(): void {
        if (!!this._currentTour.steps.some(i => !!i.temporaryElementToScroll)) {
            this._currentTour.steps
                .filter(i => !!i.temporaryElementToScroll)
                .forEach(i => {
                    try {
                        this.dom.querySelector(`#${i.temporaryElementToScroll}`).remove();
                    } catch (error) {}
                });
        }
    }

    public resetTour(): void {
        this.checkTemporaryElementToScroll();
        this.dom.body.classList.remove("tour-open");
        this._currentTour = null;
        this._currentTourStepIndex = 0;
        this._guidedTourCurrentStepSubject.next(null);
    }

    private delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /* check if there are any components to open or close before the display step */
    private goToStep(action: "start" | "next" | "back") {
        let msTimeout = false;
        const previousIndex = action === "back" ? this._currentTourStepIndex + 1 : this._currentTourStepIndex - 1;
        const previousStep = this._currentTour.steps[previousIndex];
        const currentStep = this._currentTour.steps[this._currentTourStepIndex];

        if (
            action !== "start" &&
            !!previousStep.anchorParentComponent &&
            previousStep.anchorParentComponent !== currentStep.anchorParentComponent
        ) {
            const needTimeout = this.closeParentComponent(previousStep.anchorParentComponent);
            msTimeout = needTimeout;
        }

        if (!!currentStep.anchorParentComponent) {
            const needTimeout = this.checkParentComponent(currentStep.anchorParentComponent);
            msTimeout = !msTimeout ? needTimeout : msTimeout;
        }

        this.delay(msTimeout ? 600 : 0).then(async () => {
            if (this._checkSelectorValidity()) {
                this._guidedTourCurrentStepSubject.next(await this.getPreparedTourStep(this._currentTourStepIndex));
            } else {
                switch (action) {
                    case "start":
                    case "next":
                        this.nextStep();
                        break;
                    case "back":
                        this.backStep();
                        break;
                }
            }
        });
    }

    public async startTour(tour: GuidedTour): Promise<void> {
        this._currentTour = cloneDeep(tour);
        this._currentTour.steps = this._currentTour.steps.filter(step => !step.skipStep);
        this._currentTourStepIndex = 0;
        this._setFirstAndLast();
        this._guidedTourOrbShowingSubject.next(this._currentTour.useOrb);
        if (
            this._currentTour.steps.length > 0 &&
            (!this._currentTour.minimumScreenSize || this.windowRef.nativeWindow.innerWidth >= this._currentTour.minimumScreenSize)
        ) {
            if (!this._currentTour.useOrb) {
                this.dom.body.classList.add("tour-open");
            }
            if (this._currentTour.steps[this._currentTourStepIndex].action) {
                this._currentTour.steps[this._currentTourStepIndex].action();
            }

            this.onboardingService.setoperationHistoryService(this._currentTour.tourId, this._currentTour.type, "VIEW");
            this.goToStep("start");
        }
    }

    public activateOrb(): void {
        this._guidedTourOrbShowingSubject.next(false);
        this.dom.body.classList.add("tour-open");
    }

    private _setFirstAndLast(): void {
        this._onLastStep = this._currentTour.steps.length - 1 === this._currentTourStepIndex;
        this._onFirstStep = this._currentTourStepIndex === 0;
    }

    private _checkSelectorValidity(): boolean {
        if (this._currentTour.steps[this._currentTourStepIndex].selector) {
            const selectedElement = this.dom.querySelector(this._currentTour.steps[this._currentTourStepIndex].selector);
            if (!selectedElement) {
                this.errorHandler.handleError(
                    // If error handler is configured this should not block the browser.
                    new Error(
                        `Error finding selector ${this._currentTour.steps[this._currentTourStepIndex].selector} on step ${this
                            ._currentTourStepIndex + 1} during guided tour: ${this._currentTour.tourId}`
                    )
                );
                return false;
            }
        }
        return true;
    }

    public get onLastStep(): boolean {
        return this._onLastStep;
    }

    public get onFirstStep(): boolean {
        return this._onFirstStep;
    }

    public get onResizeMessage(): boolean {
        return this._onResizeMessage;
    }

    public get currentTourStepDisplay(): number {
        return this._currentTourStepIndex + 1;
    }

    public get currentTourStepCount(): number {
        return this._currentTour && this._currentTour.steps ? this._currentTour.steps.length : 0;
    }

    public get preventBackdropFromAdvancing(): boolean {
        return this._currentTour && this._currentTour.preventBackdropFromAdvancing;
    }

    private async getPreparedTourStep(index: number): Promise<TourStep> {
        return await this.setTourOrientation(this._currentTour.steps[index]);
    }

    private isMobileSize(): boolean {
        return window.innerWidth < 764;
    }

    private getPercentage(x: number, y: number): number {
        return Math.floor((x * 100) / y);
    }

    /* calculate left style for center of selected element */
    private calculateLeftStyleArrowOnMobile(width: number, left: number): number {
        const marginTourMobile = 12;
        return Math.floor(width / 2 - marginTourMobile + left);
    }

    private calculateOrientation(convertedStep: TourStep) {
        const el = this.dom.querySelector(convertedStep.selector).getBoundingClientRect();

        if (this.isMobileSize()) {
            const heightPercentage = this.getPercentage(el.top, window.innerHeight);
            convertedStep.customStyleLeftArrow = this.calculateLeftStyleArrowOnMobile(el.width, el.left);
            convertedStep.orientation = heightPercentage >=  20 ? Orientation.Top : Orientation.Bottom;
            return convertedStep;
        }

        let orientationDesktop = null;
        const leftPercentage = this.getPercentage(el.left + el.width, window.innerWidth);
        if (leftPercentage < 35) {
            orientationDesktop = Orientation.Right;
            convertedStep.orientation = orientationDesktop;
            return convertedStep;
        }

        const rightPercentage = this.getPercentage(window.innerWidth - el.right + el.width, window.innerWidth);
        if (rightPercentage < 35) {
            orientationDesktop = Orientation.Left;
            convertedStep.orientation = orientationDesktop;
            return convertedStep;
        }

        const heightPercentage = this.getPercentage(el.top, window.innerHeight);
        orientationDesktop = heightPercentage > 20 ? Orientation.Top : Orientation.Bottom;

        convertedStep.orientation = orientationDesktop;
        return convertedStep;
    }

    private checkScrollX(convertedStep: TourStep): void {
        const el = this.dom.querySelector(convertedStep.selector).getBoundingClientRect();
        const elPositionX = el.left + el.width;
        if (elPositionX > this.windowRef.nativeWindow.innerWidth) {
            const selector: Element = this.dom.querySelector(convertedStep.selector);
            selector.scrollIntoView({ block: "end", inline: "end" });
            return;
        }
        if (el.right < 0) {
            const selector: Element = this.dom.querySelector(convertedStep.selector);
            selector.scrollIntoView({ block: "start", inline: "start" });
        }
    }

    private checkScrollY(convertedStep: TourStep): void {
        const el: IDomElement = this.dom.querySelector(convertedStep.selector).getBoundingClientRect();
        const elPositionY = el.top + el.height;

        if (elPositionY > this.windowRef.nativeWindow.innerHeight || this.isMobileSize()) {
            try {
                const temporaryElementToScroll = document.createElement("div");
                temporaryElementToScroll.id = convertedStep.selector.replace("#", "") + "-onboarding-scroll";
                temporaryElementToScroll.style.top = this.isMobileSize() ? "-180px" : "-250px";
                temporaryElementToScroll.style.position = "absolute";

                const selector: Element = this.dom.querySelector(convertedStep.selector);
                document.getElementById(convertedStep.selector.replace("#", "")).style.position = "relative";
                selector.appendChild(temporaryElementToScroll);
                document.getElementById(temporaryElementToScroll.id).scrollIntoView();
                this._currentTour.steps[this._currentTourStepIndex].temporaryElementToScroll = temporaryElementToScroll.id;
            } catch (error) {}
        }

        if (elPositionY < this.windowRef.nativeWindow.innerHeight) {
            try {
                const temporaryElementToScroll = document.createElement("div");
                temporaryElementToScroll.id = convertedStep.selector.replace("#", "") + "-onboarding-scroll";
                temporaryElementToScroll.style.top = "0";
                temporaryElementToScroll.style.position = "absolute";

                const selector: Element = this.dom.querySelector(convertedStep.selector);
                document.getElementById(convertedStep.selector.replace("#", "")).style.position = "relative";
                selector.appendChild(temporaryElementToScroll);
                document.getElementById(temporaryElementToScroll.id).scrollIntoView();
                this._currentTour.steps[this._currentTourStepIndex].temporaryElementToScroll = temporaryElementToScroll.id;
            } catch (error) {}
        }
    }

    private checkParentComponent(component: string): boolean {
        const splitComponent = component.split("#").filter(i => !!i);
        if (!splitComponent.length) {
            return false;
        }
        let hasExecutedAction = true;
        for (const i of splitComponent) {
            switch (i) {
                case "overlay-user":
                    this.store.dispatch(new SelectOverlayAction("user"));
                    break;
                case "edp-e-sidebar":
                    this.store.dispatch(new OpenSidebarAction());
                    break;
                case "edp-e-sidebar-sub-level":
                    this.store.dispatch(new OpenSidebarAction(true));
                    break;
                default:
                    hasExecutedAction = false;
                    break;
            }
        }
        return hasExecutedAction;
    }

    private closeParentComponent(component: string): boolean {
        const splitComponent = component.split("#");
        if (!splitComponent.length) {
            return false;
        }
        let hasExecutedAction = true;
        splitComponent.forEach(i => {
            switch (i) {
                case "overlay-user":
                    this.store.dispatch(new SelectOverlayAction(null));
                    break;
                case "edp-e-sidebar":
                    this.store.dispatch(new CloseSidebarAction());
                    break;
                case "edp-e-sidebar-sub-level":
                    this.store.dispatch(new CloseSidebarAction(true));
                    break;
                default:
                    hasExecutedAction = false;
                    break;
            }
        });
        return hasExecutedAction;
    }

    private changeZIndex(convertedStep: TourStep): void {
        const el = document.getElementById(convertedStep.selector.replace("#", ""));
        if (!!el) {
            el.style.zIndex = indexZ;
        }
    }

    private async setTourOrientation(step: TourStep): Promise<TourStep> {
        const convertedStep: TourStep = cloneDeep(step);

        if (this._currentTour.type === OnboardingTypeEnum.Tooltip) {
            this.changeZIndex(convertedStep);
        }
        if (!!convertedStep.selector) {
            await sleep(500);
            this.checkScrollX(convertedStep);
            this.checkScrollY(convertedStep);
        }
        if (!convertedStep.orientation && !!convertedStep.selector) {
            this.calculateOrientation(convertedStep);
        }

        if (
            convertedStep.orientation &&
            !(typeof convertedStep.orientation === "string") &&
            (convertedStep.orientation as OrientationConfiguration[]).length
        ) {
            (convertedStep.orientation as OrientationConfiguration[]).sort((a: OrientationConfiguration, b: OrientationConfiguration) => {
                if (!b.maximumSize) {
                    return 1;
                }
                if (!a.maximumSize) {
                    return -1;
                }
                return b.maximumSize - a.maximumSize;
            });

            let currentOrientation: Orientation = Orientation.Top;
            (convertedStep.orientation as OrientationConfiguration[]).forEach((orientationConfig: OrientationConfiguration) => {
                if (!orientationConfig.maximumSize || this.windowRef.nativeWindow.innerWidth <= orientationConfig.maximumSize) {
                    currentOrientation = orientationConfig.orientationDirection;
                }
            });

            convertedStep.orientation = currentOrientation;
        }
        return convertedStep;
    }
}
